/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Form from './Form';
import Buttons from './Buttons';

import { ReactComponent as ChatBubble } from '../../../SVGAssets/chat-bubble.svg';

import s from './MessageList.module.scss';
import tgai from '../TourGuideAi.module.scss';

const INTEREST_NUDGES = [
  'Great! Select 2 more to continue.',
  'Almost there! Choose 1 more.',
  'Thanks for sharing your interests!',
];

const MessageList = ({
  messages,
  formState,
  conversationState,
  isAiThinking,
  aiThinkingWithLabel,
  messagesEndRef,
  hasContentBelow,
  hasContentAbove,
  onScrollToBottom,
  unreadCount,
  handlers,
}) => {
  const { onButtonClick } = handlers;

  const lastBotIndex = messages.reduce((last, msg, i) => (msg.type === 'bot' ? i : last), -1);

  return (
    <div className={s.messageListWrapper}>
      <div className={cx(s.messageList)}>
        {messages.map((message, index) => {
          const isLatest = index === lastBotIndex;
          const isInterestSelection = message.buttons?.length > 0
            && message.buttons.every((b) => b.action === 'toggle_interest');
          const selectedCount = conversationState.selectedInterests?.length || 0;
          const displayInstructions = isLatest && isInterestSelection && selectedCount >= 1
            ? INTEREST_NUDGES[selectedCount - 1]
            : message.instructions;
          return (
          <div
            key={message.id}
            data-message
            className={cx(s.message, s[message.type], tgai.stack, tgai.m)}
            data-step-type={message.stepType}
          >
            {message.type === 'bot' && (
            <>
              <p dangerouslySetInnerHTML={{ __html: message.text }} />
              {displayInstructions && (
                <p className={s.instructions}>
                  <em>{displayInstructions}</em>
                </p>
              )}
              {message.formFields && (
              <Form
                formFields={message.formFields}
                submitButton={message.submitButton}
                clearDataLink={message.clearDataLink}
                formState={formState}
                handlers={handlers}
              />
              )}
              <Buttons
                buttons={message.buttons}
                continueButton={message.continueButton}
                links={message.links}
                conversationState={conversationState}
                onButtonClick={onButtonClick}
                isLatest={isLatest}
              />
            </>
            )}
            {message.type === 'user' && <p>{message.text}</p>}
          </div>
          );
        })}
        {isAiThinking && (
          <div className={cx(s.message, s.bot)}>
            <div className={s.thinking}>
              <div className={s.dots}>
                <span />
                <span />
                <span />
              </div>
              {aiThinkingWithLabel && <p><em>TourGuide AI is thinking</em></p>}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className={cx(s.scrollOverlay, { [s.visible]: hasContentBelow || unreadCount > 0 })}>
        {(hasContentAbove || unreadCount > 0) && (
          <button
            type="button"
            className={s.scrollIndicator}
            onClick={onScrollToBottom}
          >
            <span className={s.scrollIndicatorText}>
              {unreadCount > 0 ? (
                <>
                  {`See ${unreadCount} New Message${unreadCount === 1 ? '' : 's'}`}
                  <ChatBubble className={s.scrollIndicatorIcon} />
                </>
              ) : (
                <>
                  Jump to Bottom
                  <svg
                    className={s.scrollIndicatorArrow}
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M8 2v10M3 8l5 5 5-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              )}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

MessageList.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    text: PropTypes.string,
  })).isRequired,
  formState: PropTypes.shape({
    isFormSubmitted: PropTypes.bool.isRequired,
    savedLeadInfo: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
    }),
  }).isRequired,
  conversationState: PropTypes.shape({
    selectedInterests: PropTypes.arrayOf(PropTypes.shape({
      uuid: PropTypes.string,
      label: PropTypes.string,
    })),
    selectedAmenities: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isAiThinking: PropTypes.bool.isRequired,
  aiThinkingWithLabel: PropTypes.bool.isRequired,
  messagesEndRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
  hasContentBelow: PropTypes.bool.isRequired,
  hasContentAbove: PropTypes.bool.isRequired,
  onScrollToBottom: PropTypes.func.isRequired,
  unreadCount: PropTypes.number.isRequired,
  handlers: PropTypes.shape({
    onFormSubmit: PropTypes.func.isRequired,
    onButtonClick: PropTypes.func.isRequired,
  }).isRequired,
};

export default MessageList;
